// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  MetricDivider,
  MetricRow,
  PrimaryMetric,
  SummaryCard,
} from '@/components/summary-card';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { formatAmountWithCurrency } from '@/currency/utilities/format';
import { ScrappedAssetCurrencyStats } from '@/report/api/get-scrapped-assets-snapshot-report';
import { formatNumber } from '@/utilities/number';
import { Grid } from '@mui/material';

interface Props {
  stats: ScrappedAssetCurrencyStats;
  currencyCode: IsoCurrencyCode;
  isLoading: boolean;
}

/**
 * Renders the scrapped-asset snapshot summary cards for a single cost-currency
 * bucket. Stats are computed by the backend per currency, so no total here ever
 * mixes currencies; amounts are formatted in the bucket's isoCode.
 */
export function ScrappedAssetSummaryCards(props: Props) {
  // A null total means no asset reported a gain or loss, so it stays uncolored.
  let gainLossColor: string | undefined;
  if (props.stats.totalGainLossOnScrap !== null) {
    gainLossColor =
      Number(props.stats.totalGainLossOnScrap) >= 0
        ? 'success.main'
        : 'error.main';
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={4}>
        <SummaryCard>
          <PrimaryMetric
            label="Total scrapped assets"
            value={formatNumber(props.stats.assetCount)}
            isLoading={props.isLoading}
          />
          <MetricDivider>
            <MetricRow
              label="Complete records"
              value={formatNumber(props.stats.assetsWithCompleteAccounting)}
              color="success.main"
              isLoading={props.isLoading}
            />
            <MetricRow
              label="Incomplete records"
              value={formatNumber(props.stats.assetsWithIncompleteAccounting)}
              color="error.main"
              isLoading={props.isLoading}
            />
          </MetricDivider>
        </SummaryCard>
      </Grid>

      <Grid item xs={12} md={4}>
        <SummaryCard>
          <PrimaryMetric
            label="Total scrap value"
            value={formatAmountWithCurrency(
              props.stats.totalScrapValue,
              props.currencyCode,
              { sparse: true }
            )}
            isLoading={props.isLoading}
          />
          <MetricDivider>
            <MetricRow
              label="Book value at scrap"
              value={formatAmountWithCurrency(
                props.stats.totalBookValueAtScrap,
                props.currencyCode,
                { sparse: true }
              )}
              isLoading={props.isLoading}
            />
            <MetricRow
              label="Gain (loss) on disposal"
              value={formatAmountWithCurrency(
                props.stats.totalGainLossOnScrap,
                props.currencyCode,
                { sparse: true }
              )}
              color={gainLossColor}
              isLoading={props.isLoading}
            />
          </MetricDivider>
        </SummaryCard>
      </Grid>

      <Grid item xs={12} md={4}>
        <SummaryCard>
          <PrimaryMetric
            label="Avg. inventory days"
            value={formatNumber(props.stats.weightedInventoryDays, {
              placeholder: '—',
            })}
            isLoading={props.isLoading}
            tooltip="Weighted average number of days in inventory (dollar-weighted by asset cost basis)"
          />
          <MetricDivider>
            <MetricRow
              label="Avg. in-service days"
              value={formatNumber(props.stats.weightedServiceDays, {
                placeholder: '—',
              })}
              isLoading={props.isLoading}
              tooltip="Weighted average number of days in service (dollar-weighted by asset cost basis)"
            />
          </MetricDivider>
        </SummaryCard>
      </Grid>
    </Grid>
  );
}
